import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const questions = [
  {
    id: 'atmosphere',
    question: 'How does the world feel around you?',
    options: [
      { label: 'Ominous and shadowy', value: 'ominous and shadowy' },
      { label: 'Bright and hopeful', value: 'bright and hopeful' },
      { label: 'Chaotic and dangerous', value: 'chaotic and dangerous' },
      { label: 'Quiet and isolated', value: 'quiet and isolated' }
    ]
  },
  {
    id: 'trait',
    question: 'What is your character\'s defining trait in this moment?',
    options: [
      { label: 'Relentlessly driven', value: 'relentlessly driven' },
      { label: 'Cautious but capable', value: 'cautious but capable' },
      { label: 'Reckless and impulsive', value: 'reckless and impulsive' },
      { label: 'Observant and calculating', value: 'observant and calculating' }
    ]
  },
  {
    id: 'situation',
    question: 'What is your current predicament?',
    options: [
      { label: 'Running out of time', value: 'running out of time' },
      { label: 'Lost in unfamiliar territory', value: 'lost in unfamiliar territory' },
      { label: 'Hiding from pursuers', value: 'hiding from pursuers' },
      { label: 'Seeking answers to a mystery', value: 'seeking answers to a mystery' }
    ]
  }
];

const OnboardingQuiz = () => {
  const navigate = useNavigate();
  const { state, updateWorld } = useStory();
  const { user } = useAuth();
  const [answers, setAnswers] = useState(state.world.onboardingAnswers || {});
  const [currentStep, setCurrentStep] = useState(0);

  React.useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      updateWorld({ onboardingAnswers: answers });
      navigate('/setup/world');
    }
  };

  const currentQ = questions[currentStep];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} key={currentStep}>
        <div className="mb-8">
          <p className="text-amber-500 text-sm font-bold tracking-widest uppercase mb-2">Quiz - Step {currentStep + 1} of {questions.length}</p>
          <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">{currentQ.question}</h2>
          <p className="text-gray-900 dark:text-purple-400">This helps the AI set the perfect scene for you.</p>
        </div>

        <div className="space-y-4 mb-8">
          {currentQ.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleSelect(currentQ.id, opt.value)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                answers[currentQ.id] === opt.value
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-purple-900 dark:text-amber-100'
                  : 'border-gray-200 dark:border-purple-800 bg-white dark:bg-purple-900/50 text-purple-700 dark:text-purple-200 hover:border-purple-300 dark:hover:border-purple-600'
              }`}
            >
              <span className="text-lg font-medium">{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-100 dark:border-purple-800">
          <button
            type="button"
            onClick={() => currentStep > 0 ? setCurrentStep(prev => prev - 1) : navigate('/')}
            className="btn-secondary flex items-center space-x-2"
          >
            <SafeIcon icon={FiIcons.FiChevronLeft} />
            <span>Back</span>
          </button>
          <button
            onClick={handleNext}
            disabled={!answers[currentQ.id]}
            className="btn-primary flex items-center space-x-3 px-8 disabled:opacity-50"
          >
            <span className="text-lg">{currentStep < questions.length - 1 ? 'Next' : 'Finish Quiz'}</span>
            <SafeIcon icon={FiIcons.FiChevronRight} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingQuiz;
